import { Button, ErrorBoundary, MessageIcon } from "@rhema/ui";
import * as Sentry from "@sentry/react";
import { type ReactNode, useEffect } from "react";
import { useRouteError } from "react-router";

// What shows when part of the app fails to draw. Each level keeps the failure as small as it can:
// one message, then one chat, then the whole app with a way to reload.

/** Reports a failure. Sentry is off unless configured, and never receives message content. */
export function reportCrash(error: unknown) {
  Sentry.captureException(error);
}

function reload() {
  window.location.reload();
}

/** The whole app could not be drawn. A calm screen with a way back. */
export function AppCrash() {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-linen px-6 text-center text-ink">
      <div className="mb-1 flex h-16 w-16 items-center justify-center rounded-[20px] bg-surface-header text-ink-secondary">
        <MessageIcon size={26} />
      </div>
      <h1 className="m-0 font-serif text-[28px] font-medium tracking-[-0.01em]">Rhema needs to reload</h1>
      <p className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
        Something on this screen stopped working. Your messages are safe on your devices, and reloading
        usually puts things right.
      </p>
      <div className="mt-2 flex flex-col items-center gap-2">
        <Button size="lg" onClick={reload}>
          Reload
        </Button>
        <Button variant="ghost" onClick={() => window.location.assign("/chats")}>
          Go to your chats
        </Button>
      </div>
    </main>
  );
}

/** The router's error screen, for a failure anywhere in a route. */
export function RouteCrash() {
  const error = useRouteError();
  useEffect(() => reportCrash(error), [error]);
  return <AppCrash />;
}

/** Wraps the whole app, for failures outside the routes. */
export function AppBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary fallback={<AppCrash />} onError={reportCrash}>
      {children}
    </ErrorBoundary>
  );
}

/** One chat or thread could not be drawn. The rest of the app keeps working. */
export function ChatBoundary({ resetKey, children }: { resetKey: string; children: ReactNode }) {
  return (
    <ErrorBoundary
      resetKey={resetKey}
      onError={reportCrash}
      fallback={(retry) => (
        <div
          role="alert"
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-chat px-6 py-12 text-center"
        >
          <h2 className="m-0 font-serif text-[24px] font-medium">This chat could not be shown</h2>
          <p className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
            Something in it stopped the screen from drawing. Your messages are safe.
          </p>
          <div className="mt-1 flex gap-2">
            <Button onClick={retry}>Try again</Button>
            <Button variant="ghost" onClick={reload}>
              Reload
            </Button>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export const ROW_CRASH = "This message could not be shown.";

/** One message could not be drawn. It shows a short note and the others carry on. */
export function MessageBoundary({ own, children }: { own: boolean; children: ReactNode }) {
  return (
    <ErrorBoundary
      onError={reportCrash}
      fallback={
        <p
          className={`m-0 max-w-[78%] rounded-[18px] bg-surface px-3.5 py-2 text-[14px] text-ink-meta italic ${own ? "self-end" : "self-start"}`}
        >
          {ROW_CRASH}
        </p>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
